// src/pages/Auth/Register.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Check, Building2, User, Lock, Mail, Phone, MapPin, ChevronRight, ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AuthService from '../../services/authService';
import { BRAND_CONFIG, getLogoSrc } from '../../config/brandConfig';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ThemeToggle, LanguageToggle } from '../../components/common/UiControls';

const cities = ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana', 'Konya', 'Gaziantep', 'Kocaeli', 'Mersin', 'Diyarbakır', 'Manisa', 'Kayseri', 'Samsun', 'Balıkesir', 'Sakarya', 'Aydın', 'Denizli'];

const inputBase = 'w-full h-12 px-4 rounded-xl border text-sm transition-all outline-none bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#24299d]/20 focus:border-[#24299d] dark:focus:border-[#5a61e0]';
const inputError = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';
const inputOk = 'border-gray-200 dark:border-white/10';
const labelClass = 'block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5';
const errClass = 'mt-1 text-xs text-red-500';
const selectBase = `${inputBase} cursor-pointer`;

const Field = ({ label, error, children }) => (
  <div>
    {label && <label className={labelClass}>{label}</label>}
    {children}
    {error && <p className={errClass}>{error}</p>}
  </div>
);

const Register = () => {
  const navigate = useNavigate();
  const { darkMode } = useAppTheme();
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [showCPw, setShowCPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState({});

  const logoSrc = getLogoSrc(darkMode);
  const v = t('auth.validation');

  const stepSchemas = [
    yup.object({
      companyName: yup.string().required(v.companyNameRequired).min(2, v.companyNameMin),
      taxNumber: yup.string().required(v.taxNumberRequired).length(10, v.taxNumberLength),
      companyType: yup.string().required(v.companyTypeRequired),
      businessArea: yup.string().required(v.businessAreaRequired),
      address: yup.string().required(v.addressRequired).min(10, v.addressMin),
      city: yup.string().required(v.cityRequired),
    }),
    yup.object({
      name: yup.string().required(v.firstNameRequired).min(2, v.firstNameMin),
      surname: yup.string().required(v.lastNameRequired).min(2, v.lastNameMin),
      title: yup.string().required(v.titleRequired),
      email: yup.string().required(v.emailRequired).email(v.emailInvalid),
      phone: yup.string().required(v.phoneRequired).matches(/^[0-9]{10}$/, v.phoneInvalid),
    }),
    yup.object({
      password: yup.string().required(v.passwordRequired).min(8, v.passwordMin).matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, v.passwordStrength),
      confirmPassword: yup.string().required(v.confirmPasswordRequired).oneOf([yup.ref('password')], v.passwordsNotMatch),
      termsAccepted: yup.boolean().oneOf([true], v.termsRequired),
    }),
  ];

  const steps = [
    { label: t('auth.step.companyInfo'), icon: Building2 },
    { label: t('auth.step.contact'), icon: User },
    { label: t('auth.step.security'), icon: Lock },
  ];

  const companyTypes = t('auth.companyTypes');
  const businessAreas = t('auth.businessAreas');

  const { register, handleSubmit, trigger, watch, formState: { errors } } = useForm({
    resolver: yupResolver(stepSchemas[step]),
    mode: 'onChange',
    defaultValues: {
      companyName: '', taxNumber: '', companyType: '', businessArea: '', address: '', city: '',
      name: '', surname: '', title: '', email: '', phone: '',
      password: '', confirmPassword: '', termsAccepted: false,
      ...saved,
    },
  });

  const onNext = async () => {
    const ok = await trigger();
    if (!ok) return;
    const vals = watch();
    setSaved((prev) => ({ ...prev, ...vals }));
    setStep((s) => s + 1);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const finalData = { ...saved, ...data };
    try {
      const result = await AuthService.register(finalData);
      if (result.success) {
        window.showToast?.('Registration completed successfully. Please sign in.', 'success');
        navigate('/login', { state: { message: t('auth.passwordUpdatedSignIn') } });
      } else {
        window.showToast?.(result.message || 'Registration failed.', 'error');
      }
    } catch {
      window.showToast?.(t('auth.serverError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / (steps.length - 1)) * 100;

  const StepContent = () => {
    if (step === 0) return (
      <div className="space-y-4">
        <Field label={t('auth.companyName')} error={errors.companyName?.message}>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input {...register('companyName')} placeholder={t('auth.companyNamePlaceholder')} className={`${inputBase} pl-10 ${errors.companyName ? inputError : inputOk}`} />
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('auth.taxNumber')} error={errors.taxNumber?.message}>
            <input {...register('taxNumber')} maxLength={10} placeholder="1234567890" className={`${inputBase} ${errors.taxNumber ? inputError : inputOk}`} />
          </Field>
          <Field label={t('auth.companyType')} error={errors.companyType?.message}>
            <select {...register('companyType')} className={`${selectBase} ${errors.companyType ? inputError : inputOk}`}>
              <option value="">{t('common.select')}</option>
              {Array.isArray(companyTypes) && companyTypes.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('auth.businessArea')} error={errors.businessArea?.message}>
            <select {...register('businessArea')} className={`${selectBase} ${errors.businessArea ? inputError : inputOk}`}>
              <option value="">{t('common.select')}</option>
              {Array.isArray(businessAreas) && businessAreas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label={t('auth.city')} error={errors.city?.message}>
            <select {...register('city')} className={`${selectBase} ${errors.city ? inputError : inputOk}`}>
              <option value="">{t('common.select')}</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t('auth.address')} error={errors.address?.message}>
          <div className="relative">
            <MapPin className="absolute left-3 top-3.5 text-gray-400" size={18} />
            <textarea {...register('address')} rows={3} placeholder={t('auth.addressPlaceholder')} className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm transition-all outline-none resize-none bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#24299d]/20 focus:border-[#24299d] ${errors.address ? inputError : inputOk}`} />
          </div>
        </Field>
      </div>
    );

    if (step === 1) return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('auth.firstName')} error={errors.name?.message}>
            <input {...register('name')} placeholder={t('auth.firstNamePlaceholder')} className={`${inputBase} ${errors.name ? inputError : inputOk}`} />
          </Field>
          <Field label={t('auth.lastName')} error={errors.surname?.message}>
            <input {...register('surname')} placeholder={t('auth.lastNamePlaceholder')} className={`${inputBase} ${errors.surname ? inputError : inputOk}`} />
          </Field>
        </div>
        <Field label={t('auth.title')} error={errors.title?.message}>
          <input {...register('title')} placeholder={t('auth.titlePlaceholder')} className={`${inputBase} ${errors.title ? inputError : inputOk}`} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('auth.email')} error={errors.email?.message}>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="email" {...register('email')} placeholder={t('auth.emailPlaceholder')} className={`${inputBase} pl-10 ${errors.email ? inputError : inputOk}`} />
            </div>
          </Field>
          <Field label={t('auth.phone')} error={errors.phone?.message}>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input {...register('phone')} placeholder="5XXXXXXXXX" maxLength={10} className={`${inputBase} pl-10 ${errors.phone ? inputError : inputOk}`} />
            </div>
          </Field>
        </div>
      </div>
    );

    return (
      <div className="space-y-4">
        <Field label={t('auth.password')} error={errors.password?.message}>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type={showPw ? 'text' : 'password'} {...register('password')} placeholder="••••••••" className={`${inputBase} pl-10 pr-12 ${errors.password ? inputError : inputOk}`} />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-400">{t('auth.passwordHint')}</p>
        </Field>
        <Field label={t('auth.confirmPassword')} error={errors.confirmPassword?.message}>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type={showCPw ? 'text' : 'password'} {...register('confirmPassword')} placeholder="••••••••" className={`${inputBase} pl-10 pr-12 ${errors.confirmPassword ? inputError : inputOk}`} />
            <button type="button" onClick={() => setShowCPw(!showCPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showCPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Field>
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm text-blue-700 dark:text-blue-300">
          {t('auth.importantNote')}
        </div>
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" {...register('termsAccepted')} className="mt-0.5 w-4 h-4 rounded accent-[#24299d]" />
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('auth.termsAccept')}</span>
          </label>
          {errors.termsAccepted && <p className={errClass}>{errors.termsAccepted.message}</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-4 font-sans">
      <div className="w-full max-w-lg">
        <div className="flex justify-end items-center gap-2 mb-4">
          <LanguageToggle />
          <ThemeToggle />
        </div>

        <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl shadow-xl p-7 border border-gray-100 dark:border-white/10">
          <div className="text-center mb-6">
            <img src={logoSrc} alt={BRAND_CONFIG.name} className="w-3/4 h-16 object-contain mx-auto mb-4" onError={(e) => { e.target.style.display = 'none'; }} />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('auth.registerTitle')}</h2>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const done = i < step;
                const active = i === step;
                return (
                  <React.Fragment key={s.label}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${done ? 'bg-green-500' : active ? 'bg-[#24299d] shadow-lg shadow-[#24299d]/30' : 'bg-gray-200 dark:bg-white/10'}`}>
                        {done ? <Check size={16} className="text-white" /> : <Icon size={16} className={active ? 'text-white' : 'text-gray-400'} />}
                      </div>
                      <span className={`text-[10px] font-semibold ${active ? 'text-[#24299d]' : done ? 'text-green-500' : 'text-gray-400'}`}>{s.label}</span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 mb-4 rounded-full transition-all ${i < step ? 'bg-green-500' : 'bg-gray-200 dark:bg-white/10'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="w-full h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#24299d] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <form onSubmit={step === steps.length - 1 ? handleSubmit(onSubmit) : (e) => e.preventDefault()}>
            <StepContent />

            <div className="flex items-center justify-between mt-6">
              {step > 0 ? (
                <button type="button" onClick={() => setStep((s) => s - 1)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                  <ChevronLeft size={16} /> {t('common.back')}
                </button>
              ) : (
                <div />
              )}

              {step < steps.length - 1 ? (
                <button type="button" onClick={onNext} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#24299d] hover:bg-[#1b2080] text-white text-sm font-semibold shadow-lg shadow-[#24299d]/20 transition-all active:scale-95">
                  {t('common.next')} <ChevronRight size={16} />
                </button>
              ) : (
                <button type="submit" disabled={loading} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow-lg shadow-green-600/20 transition-all active:scale-95 disabled:opacity-60">
                  {loading ? t('auth.saving') : <><Check size={16} /> {t('auth.completeRegistration')}</>}
                </button>
              )}
            </div>
          </form>

          <div className="mt-5 pt-5 border-t border-gray-100 dark:border-white/10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('auth.alreadyHaveAccount')}{' '}
              <button type="button" onClick={() => navigate('/login')} className="font-semibold text-[#24299d] hover:text-[#1b2080] dark:text-[#818cf8]">
                {t('auth.signIn')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
